import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Command as CommandPrimitive } from "cmdk";
import { ArrowUp, Building2, ClipboardList, Inbox, Paperclip, Square, X } from "lucide-react";
import { api } from "@/lib/api";
import { STATUS } from "@/lib/format";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

/* ------------------------------------------------------------------
   Composer: a floating card with an auto-growing textarea. Enter sends,
   Shift+Enter breaks the line. Optional record context (order, customer,
   inquiry) is attached as a chip and sent along with the message.
   ------------------------------------------------------------------ */

const EMPTY = { orders: [], customers: [], inquiries: [] };
const GROUP = "[&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:pb-1 [&_[cmdk-group-heading]]:pt-2 [&_[cmdk-group-heading]]:text-[11.5px] [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:text-muted-foreground";
const ITEM = "flex cursor-default select-none items-center gap-2.5 rounded-lg px-2 py-1.5 text-[13px] outline-none data-[selected=true]:bg-accent/70";

export function ContextPicker({ value, onChange, disabled }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [res, setRes] = useState(EMPTY);
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (!open) { setQ(""); setRes(EMPTY); } }, [open]);
  useEffect(() => {
    const term = q.trim();
    if (term.length < 2) { setRes(EMPTY); return undefined; }
    let alive = true;
    setBusy(true);
    const t = setTimeout(() => {
      const enc = encodeURIComponent(term);
      Promise.all([
        api.get(`/drift/orders?q=${enc}`).then((r) => r.data?.items || []).catch(() => []),
        api.get(`/drift/customers?q=${enc}`).then((r) => (Array.isArray(r.data) ? r.data : r.data?.items || [])).catch(() => []),
        api.get(`/drift/inquiries?q=${enc}`).then((r) => r.data?.items || []).catch(() => []),
      ]).then(([orders, customers, inquiries]) => { if (!alive) return; setRes({ orders: orders.slice(0, 5), customers: customers.slice(0, 4), inquiries: inquiries.slice(0, 4) }); setBusy(false); });
    }, 160);
    return () => { alive = false; clearTimeout(t); };
  }, [q]);

  const pick = (ctx) => { onChange(ctx); setOpen(false); };
  const n = res.orders.length + res.customers.length + res.inquiries.length;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <Tooltip delayDuration={300}>
        <TooltipTrigger asChild>
          <PopoverTrigger asChild>
            <button type="button" disabled={disabled} className={cn("inline-flex h-8 items-center gap-1.5 rounded-lg px-2 text-[12.5px] font-medium text-muted-foreground transition-colors hover:bg-accent/60 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50", value && "text-foreground")} data-testid="ai-context-button" aria-label="Legg til kontekst">
              <Paperclip className="h-4 w-4" strokeWidth={1.75} aria-hidden /> <span className="hidden sm:inline">Kontekst</span>
            </button>
          </PopoverTrigger>
        </TooltipTrigger>
        <TooltipContent side="top" className="rounded-lg text-[12.5px]">Hekt på eit oppdrag, ein kunde eller ei henvending</TooltipContent>
      </Tooltip>
      <PopoverContent align="start" sideOffset={10} className="shadow-pop w-[380px] rounded-xl border-0 p-0" data-testid="ai-context-popover">
        <CommandPrimitive shouldFilter={false} loop className="flex flex-col">
          <div className="border-b border-border/70 px-3">
            <CommandPrimitive.Input value={q} onValueChange={setQ} autoFocus placeholder="Søk etter oppdrag, kunde eller henvending …" className="h-10 w-full bg-transparent text-[13.5px] outline-none placeholder:text-muted-foreground/70" data-testid="ai-context-search" />
          </div>
          <CommandPrimitive.List className="max-h-[300px] overflow-y-auto p-1.5">
            {q.trim().length < 2 && <p className="px-2 py-5 text-center text-[12.5px] text-muted-foreground">Skriv minst to teikn.</p>}
            {q.trim().length >= 2 && !busy && n === 0 && <p className="px-2 py-5 text-center text-[12.5px] text-muted-foreground">Ingen treff.</p>}
            {busy && n === 0 && q.trim().length >= 2 && <p className="px-2 py-5 text-center text-[12.5px] text-muted-foreground">Søkjer …</p>}
            {res.orders.length > 0 && (
              <CommandPrimitive.Group heading="Oppdrag" className={GROUP}>
                {res.orders.map((o) => (
                  <CommandPrimitive.Item key={o.id} value={`o-${o.id}`} className={ITEM} onSelect={() => pick({ type: "order", id: o.id, label: `${o.ref} · ${o.title}`, link: `/drift/oppdrag/${o.id}` })} data-testid="ai-context-result">
                    <ClipboardList className="h-4 w-4 shrink-0 text-muted-foreground" strokeWidth={1.75} aria-hidden />
                    <span className="min-w-0 flex-1 truncate">{o.title} <span className="text-muted-foreground">· {o.customer_name}</span></span>
                    <span className="shrink-0 font-mono text-[11.5px] text-muted-foreground">{o.ref} · {STATUS[o.status] || o.status}</span>
                  </CommandPrimitive.Item>
                ))}
              </CommandPrimitive.Group>
            )}
            {res.customers.length > 0 && (
              <CommandPrimitive.Group heading="Kundar" className={GROUP}>
                {res.customers.map((c) => (
                  <CommandPrimitive.Item key={c.id} value={`c-${c.id}`} className={ITEM} onSelect={() => pick({ type: "customer", id: c.id, label: c.name, link: `/drift/kundar/${c.id}` })} data-testid="ai-context-result">
                    <Building2 className="h-4 w-4 shrink-0 text-muted-foreground" strokeWidth={1.75} aria-hidden />
                    <span className="min-w-0 flex-1 truncate">{c.name} {c.address && <span className="text-muted-foreground">· {c.address}</span>}</span>
                  </CommandPrimitive.Item>
                ))}
              </CommandPrimitive.Group>
            )}
            {res.inquiries.length > 0 && (
              <CommandPrimitive.Group heading="Henvendingar" className={GROUP}>
                {res.inquiries.map((i) => (
                  <CommandPrimitive.Item key={i.id} value={`i-${i.id}`} className={ITEM} onSelect={() => pick({ type: "inquiry", id: i.id, label: `${i.ref} · ${i.company || i.name}`, link: `/drift/henvendingar/${i.id}` })} data-testid="ai-context-result">
                    <Inbox className="h-4 w-4 shrink-0 text-muted-foreground" strokeWidth={1.75} aria-hidden />
                    <span className="min-w-0 flex-1 truncate">{i.company || i.name} <span className="text-muted-foreground">· {i.message}</span></span>
                  </CommandPrimitive.Item>
                ))}
              </CommandPrimitive.Group>
            )}
          </CommandPrimitive.List>
        </CommandPrimitive>
      </PopoverContent>
    </Popover>
  );
}

export function Composer({ value, onChange, onSend, onStop, streaming, context, onContext, placeholder = "Spør om oppdrag, kundar og plan – eller be om eit utkast …", model, autoFocus = true, compact = false, className }) {
  const ref = useRef(null);
  const canSend = value.trim().length > 0 && !streaming;

  // Auto-grow up to ~9 lines.
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = "0px";
    const max = compact ? 160 : 220;
    el.style.height = `${Math.min(max, Math.max(compact ? 24 : 28, el.scrollHeight))}px`;
  }, [value, compact]);

  const onKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent?.isComposing) {
      e.preventDefault();
      if (canSend) onSend();
    }
  };

  return (
    <div className={cn("shadow-pop rounded-2xl bg-card", className)} data-testid="ai-composer">
      {context && (
        <div className="flex items-center gap-2 px-3 pt-3">
          <span className="inline-flex h-7 max-w-full items-center gap-1.5 rounded-lg bg-secondary pl-2.5 pr-1 text-[12px] font-medium" data-testid="ai-context-chip">
            <Paperclip className="h-3 w-3 shrink-0 text-muted-foreground" strokeWidth={1.75} aria-hidden />
            <span className="truncate">{context.label}</span>
            <button type="button" onClick={() => onContext(null)} className="inline-flex h-5 w-5 items-center justify-center rounded-md text-muted-foreground hover:bg-card hover:text-foreground" aria-label="Fjern kontekst" data-testid="ai-context-remove"><X className="h-3 w-3" strokeWidth={2} /></button>
          </span>
        </div>
      )}
      <textarea
        ref={ref}
        rows={1}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={onKey}
        placeholder={placeholder}
        autoFocus={autoFocus}
        className={cn("block w-full resize-none bg-transparent px-4 text-[15px] leading-[1.55] text-foreground outline-none placeholder:text-muted-foreground/60 scrollbar-thin", compact ? "pt-3" : "pt-3.5")}
        aria-label="Melding til assistenten"
        data-testid="ai-composer-input"
      />
      <div className="flex items-center justify-between gap-2 px-2 pb-2 pt-1">
        <div className="flex items-center gap-0.5">
          <ContextPicker value={context} onChange={onContext} disabled={streaming} />
        </div>
        <div className="flex items-center gap-2">
          {model && <span className="hidden font-mono text-[11px] text-muted-foreground sm:inline" data-testid="ai-model-label">{model}</span>}
          {streaming ? (
            <button type="button" onClick={onStop} className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-foreground text-background transition-transform duration-150 hover:scale-[1.04] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2" aria-label="Stopp" data-testid="ai-stop-button">
              <Square className="h-3.5 w-3.5 fill-current" strokeWidth={0} />
            </button>
          ) : (
            <button type="button" onClick={onSend} disabled={!canSend} className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-brand text-brand-foreground transition-[transform,opacity,background-color] duration-150 hover:bg-brand-dark enabled:hover:scale-[1.04] disabled:opacity-35 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2" aria-label="Send" data-testid="ai-send-button">
              <ArrowUp className="h-4 w-4" strokeWidth={2.25} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
